import {
  AndroidOutlined,
  AppleOutlined,
  AppstoreOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  EditOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FolderAddOutlined,
  FolderOutlined,
  InboxOutlined,
  LockOutlined,
  LogoutOutlined,
  MobileOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  SettingOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  Divider,
  Empty,
  Form,
  Input,
  Layout,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ChecklistState,
  ProjectSummary,
  PublicUser,
  RunStatus,
  RunSummary,
  TaskDetail,
  TaskPlatform,
  TaskSummary,
  TestResult,
  UploadStatus,
} from '../shared/api';
import { isActiveRun } from '../shared/api';
import { ChecklistEditor } from './ChecklistEditor';
import { ApiError, api, getToken, reportUrl, setToken } from './api';
import carbsiLogo from './assets/carbsi-emblem.png';

const { Header, Sider: SideNav, Content } = Layout;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('读取文件失败，请重试'));
    reader.readAsDataURL(file);
  });
}

function LoginView({ onSignedIn }: { onSignedIn: (user: PublicUser) => void }) {
  const [pending, setPending] = useState(false);
  const [form] = Form.useForm();

  return (
    <div className="login-page-wrapper">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo-wrap">
            <img
              alt="碳硅云测 Logo"
              className="login-logo-img"
              src={carbsiLogo}
            />
          </div>
          <h1 className="login-brand-title">碳硅云测</h1>
          <div className="login-brand-slogan">
            基于多模态 AI 的智能移动与全端测试云平台
          </div>
          <div
            style={{
              fontSize: 11,
              color: '#94a3b8',
              marginTop: 4,
              letterSpacing: '0.5px',
            }}
          >
            CarbsiLab · Cloud Testing Infrastructure
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={async (values: { username: string; password: string }) => {
            setPending(true);
            try {
              const session = await api.login(values.username, values.password);
              setToken(session.token);
              message.success(`欢迎回来，${session.user.username}`);
              onSignedIn(session.user);
            } catch (error) {
              message.error(
                error instanceof Error
                  ? error.message
                  : '登录失败，请检查用户名和密码',
              );
            } finally {
              setPending(false);
            }
          }}
        >
          <Form.Item
            label="账号用户名"
            name="username"
            rules={[{ required: true, message: '请输入登录用户名' }]}
          >
            <Input
              autoComplete="username"
              placeholder="请输入用户名（如 admin）"
              prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="登录密码"
            name="password"
            rules={[{ required: true, message: '请输入登录密码' }]}
          >
            <Input.Password
              autoComplete="current-password"
              placeholder="请输入密码"
              prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              size="large"
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              block
              className="login-btn"
              htmlType="submit"
              loading={pending}
              size="large"
              type="primary"
            >
              立即登录
            </Button>
          </Form.Item>
        </Form>

        <div className="login-footer-copyright">
          碳硅实验室 © 2026 CarbsiLab · 驱动下一代智能化测试
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [ready, setReady] = useState(!getToken());
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectId, setProjectId] = useState<string>();
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [section, setSection] = useState<'tasks' | 'members' | 'users'>(
    'tasks',
  );
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      const result = await api.projects();
      setProjects(result.projects);
      setProjectId((current) => current ?? result.projects[0]?.id);
    } catch (error) {
      console.error('加载项目列表失败:', error);
    }
  }, []);

  const loadTasks = useCallback(async (targetProjectId: string) => {
    try {
      const result = await api.tasks(targetProjectId);
      setTasks(result.tasks);
    } catch (error) {
      console.error('加载任务失败:', error);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    void api
      .me()
      .then(async ({ user: nextUser }) => {
        setUser(nextUser);
        await loadProjects();
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) setToken(null);
        setUser(null);
      })
      .finally(() => setReady(true));
  }, [loadProjects]);

  useEffect(() => {
    if (!projectId || !user) return;
    void loadTasks(projectId);
  }, [projectId, user, loadTasks]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId),
    [projectId, projects],
  );

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      message.warning('请输入项目名称');
      return;
    }
    setCreatingProject(true);
    try {
      const created = await api.createProject(newProjectName.trim());
      message.success(`项目「${created.project.name}」创建成功`);
      setNewProjectName('');
      setNewProjectModalOpen(false);
      await loadProjects();
      setProjectId(created.project.id);
      setTask(null);
      setSection('tasks');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建项目失败');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleSignOut = async () => {
    await api.logout().catch(() => undefined);
    setToken(null);
    setUser(null);
    message.info('已安全退出登录');
  };

  if (!ready) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" tip="正在初始化碳硅云测平台..." />
      </div>
    );
  }

  if (!user) {
    return (
      <ConfigProvider
        locale={zhCN}
        theme={{
          token: {
            colorPrimary: '#4f46e5',
            borderRadius: 8,
          },
        }}
      >
        <LoginView onSignedIn={setUser} />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#4f46e5',
          borderRadius: 8,
          colorLink: '#4f46e5',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
        },
      }}
    >
      <Layout className="shell">
        {/* Light Sidebar */}
        <SideNav className="app-sidebar" theme="light" width={260}>
          <div className="sidebar-content">
            {/* Brand Logo & Name */}
            <div className="brand-container">
              <div className="brand-logo-frame">
                <img
                  alt="碳硅云测 Logo"
                  className="brand-logo-img"
                  src={carbsiLogo}
                />
              </div>
              <div className="brand-text-wrap">
                <div className="brand-title-main">碳硅云测</div>
                <div className="brand-title-sub">CarbsiLab · 智能测试</div>
              </div>
            </div>

            {/* Project List Header */}
            <div className="sidebar-section-header">
              <span className="sidebar-section-title">
                项目空间 ({projects.length})
              </span>
              {user.role === 'admin' ? (
                <Tooltip title="新建测试项目">
                  <Button
                    className="sidebar-add-btn"
                    icon={<PlusOutlined />}
                    onClick={() => setNewProjectModalOpen(true)}
                    size="small"
                    type="text"
                  />
                </Tooltip>
              ) : null}
            </div>

            {/* Project Selection Menu */}
            <div className="sidebar-project-list">
              {projects.map((project) => {
                const isActive = project.id === projectId;
                return (
                  <div className="project-swipe-row" key={project.id}>
                    <button
                      type="button"
                      className={`project-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        setProjectId(project.id);
                        setTask(null);
                        setSection('tasks');
                      }}
                    >
                      <FolderOutlined className="project-icon" />
                      <span className="project-name" title={project.name}>
                        {project.name}
                      </span>
                      {isActive ? (
                        <span className="project-active-dot" />
                      ) : null}
                    </button>
                    {user.role === 'admin' ? (
                      <Popconfirm
                        title={`删除项目「${project.name}」？`}
                        description="项目内所有任务、执行记录和报告将永久删除，无法恢复。"
                        okText="删除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                        onConfirm={async () => {
                          try {
                            await api.deleteProject(project.id);
                            setProjects((current) =>
                              current.filter((item) => item.id !== project.id),
                            );
                            if (isActive) {
                              setProjectId(
                                projects.find((item) => item.id !== project.id)
                                  ?.id,
                              );
                              setTasks([]);
                              setTask(null);
                              setSection('tasks');
                            }
                            message.success('项目已删除');
                          } catch (error) {
                            message.error(
                              error instanceof Error
                                ? error.message
                                : '删除项目失败',
                            );
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="project-delete"
                          aria-label={`删除项目 ${project.name}`}
                        >
                          删除
                        </button>
                      </Popconfirm>
                    ) : null}
                  </div>
                );
              })}

              {projects.length === 0 ? (
                <div
                  style={{
                    padding: '24px 12px',
                    textAlign: 'center',
                    color: '#64748b',
                    fontSize: 13,
                  }}
                >
                  暂无项目空间
                </div>
              ) : null}
            </div>

            {/* Sidebar User Footer */}
            <div className="sidebar-footer">
              <div className="user-profile-info">
                <div className="user-avatar-badge">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div className="user-text-info">
                  <span className="username-display">{user.username}</span>
                  <span className="user-role-badge">
                    {user.role === 'admin' ? '平台管理员' : '测试工程师'}
                  </span>
                </div>
              </div>
              <Tooltip title="退出登录">
                <Popconfirm
                  cancelText="取消"
                  okText="退出"
                  onConfirm={handleSignOut}
                  placement="topRight"
                  title="确认退出当前账号？"
                >
                  <Button
                    className="sidebar-logout-btn"
                    icon={<LogoutOutlined />}
                    size="small"
                    type="text"
                  />
                </Popconfirm>
              </Tooltip>
            </div>
          </div>
        </SideNav>

        {/* Main Workspace Layout */}
        <Layout className="main-layout">
          {/* Header Bar */}
          <Header className="app-header">
            <div className="header-left">
              <div className="header-project-title">
                <FolderOutlined style={{ color: '#4f46e5' }} />
                <span>{selectedProject?.name ?? '请选择项目'}</span>
                {selectedProject ? (
                  <Tag color="cyan" style={{ marginLeft: 6, borderRadius: 4 }}>
                    云测就绪
                  </Tag>
                ) : null}
              </div>
            </div>

            {selectedProject ? (
              <div className="header-nav-tabs">
                <button
                  className={`header-tab-btn ${section === 'tasks' ? 'active' : ''}`}
                  onClick={() => {
                    setSection('tasks');
                    setTask(null);
                  }}
                  type="button"
                >
                  <AppstoreOutlined />
                  测试任务
                  <Badge
                    count={tasks.length}
                    style={{
                      backgroundColor:
                        section === 'tasks' ? '#4f46e5' : '#94a3b8',
                      marginLeft: 4,
                    }}
                  />
                </button>
                <button
                  className={`header-tab-btn ${section === 'members' ? 'active' : ''}`}
                  onClick={() => {
                    setSection('members');
                    setTask(null);
                  }}
                  type="button"
                >
                  <TeamOutlined />
                  项目成员
                </button>
                {user.role === 'admin' ? (
                  <button
                    className={`header-tab-btn ${section === 'users' ? 'active' : ''}`}
                    onClick={() => {
                      setSection('users');
                      setTask(null);
                      void api.users().then((result) => setUsers(result.users));
                    }}
                    type="button"
                  >
                    <SettingOutlined />
                    用户管理
                  </button>
                ) : null}
              </div>
            ) : null}
          </Header>

          {/* Main Content Body */}
          <Content className="app-content">
            {!selectedProject ? (
              <Card className="workspace-card">
                <Empty
                  description="暂未选择或创建项目空间，请在左侧侧边栏中新建或选择一个项目开始测试。"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  {user.role === 'admin' ? (
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => setNewProjectModalOpen(true)}
                      type="primary"
                    >
                      新建测试项目
                    </Button>
                  ) : null}
                </Empty>
              </Card>
            ) : (
              <>
                {section === 'tasks' && !task ? (
                  <TaskList
                    canCreate
                    onCreated={async () => {
                      await loadTasks(selectedProject.id);
                    }}
                    onDeleted={(id) =>
                      setTasks((current) => current.filter((t) => t.id !== id))
                    }
                    onEdit={(id) => setEditingTaskId(id)}
                    onOpen={(id) => {
                      void api.task(id).then((result) => setTask(result.task));
                    }}
                    projectId={selectedProject.id}
                    tasks={tasks}
                  />
                ) : null}

                {section === 'tasks' && task ? (
                  <TaskDetailView
                    key={task.id}
                    onChecklistSaved={(state) =>
                      setTask((current) =>
                        current ? { ...current, ...state } : current,
                      )
                    }
                    onBack={() => setTask(null)}
                    onEdit={() => setEditingTaskId(task.id)}
                    onRefresh={async () => {
                      const res = await api.task(task.id);
                      setTask(res.task);
                    }}
                    task={task}
                    token={getToken() ?? ''}
                  />
                ) : null}

                {section === 'members' && user.role === 'admin' ? (
                  <MembersView projectId={selectedProject.id} />
                ) : null}

                {section === 'members' && user.role !== 'admin' ? (
                  <Card className="workspace-card">
                    <Empty
                      description="您当前为项目成员角色，如需新增或调整成员权限，请联系管理员处理。"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                ) : null}

                {section === 'users' && user.role === 'admin' ? (
                  <UsersView
                    onCreated={(created) =>
                      setUsers((current) => [...current, created])
                    }
                    users={users}
                  />
                ) : null}
              </>
            )}
          </Content>
        </Layout>
      </Layout>

      {/* New Project Modal */}
      <Modal
        confirmLoading={creatingProject}
        destroyOnClose
        okText="立即创建"
        onCancel={() => {
          setNewProjectModalOpen(false);
          setNewProjectName('');
        }}
        onOk={handleCreateProject}
        open={newProjectModalOpen}
        title="新建测试项目"
      >
        <div style={{ margin: '16px 0 8px', color: '#475569', fontSize: 13 }}>
          项目用于隔离测试需求、多平台任务执行记录与自动化用例清单。
        </div>
        <Input
          autoFocus
          onChange={(event) => setNewProjectName(event.target.value)}
          onPressEnter={handleCreateProject}
          placeholder="例如：移动电商 App 自动化测试"
          size="large"
          value={newProjectName}
        />
      </Modal>

      {/* Edit Task Modal */}
      <EditTaskModal
        onCancel={() => setEditingTaskId(null)}
        onUpdated={async (updatedTask) => {
          if (selectedProject) {
            await loadTasks(selectedProject.id);
          }
          if (task && task.id === updatedTask.id) {
            setTask(updatedTask);
          }
        }}
        open={Boolean(editingTaskId)}
        taskId={editingTaskId}
      />
    </ConfigProvider>
  );
}

function TaskList({
  projectId,
  tasks,
  onOpen,
  onEdit,
  onCreated,
  onDeleted,
  canCreate,
}: {
  projectId: string;
  tasks: TaskSummary[];
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onCreated: () => Promise<void>;
  onDeleted: (id: string) => void;
  canCreate: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState<TaskSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [docxBase64, setDocxBase64] = useState<string>();
  const [docxFileName, setDocxFileName] = useState<string>();
  const [inputMode, setInputMode] = useState<'text' | 'docx'>('text');
  const [platformFilter, setPlatformFilter] = useState<
    'all' | 'android' | 'ios'
  >('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Metrics computation
  const metrics = useMemo(() => {
    const total = tasks.length;
    const checklistReady = tasks.filter((t) => t.hasChecklist).length;
    const running = tasks.filter(
      (t) =>
        t.currentRun &&
        ['claimed', 'preparing', 'running'].includes(t.currentRun.status),
    ).length;
    const finishedWithResult = tasks.filter((t) => t.currentRun?.result);
    const passedCount = finishedWithResult.filter(
      (t) => t.currentRun?.result === 'passed',
    ).length;
    const passRate =
      finishedWithResult.length > 0
        ? Math.round((passedCount / finishedWithResult.length) * 100)
        : 100;
    return { total, checklistReady, running, passRate };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (platformFilter !== 'all' && task.platform !== platformFilter)
        return false;
      if (
        searchKeyword.trim() &&
        !task.title.toLowerCase().includes(searchKeyword.toLowerCase())
      )
        return false;
      return true;
    });
  }, [tasks, platformFilter, searchKeyword]);

  const renderRunStatus = (run: RunSummary | null) => {
    if (!run) {
      return (
        <Tag color="default" style={{ borderRadius: 4 }}>
          未执行
        </Tag>
      );
    }

    const statusMap: Record<
      RunStatus,
      { text: string; color: string; isRunning?: boolean }
    > = {
      claimed: { text: '已认领', color: 'purple' },
      preparing: { text: '准备中', color: 'indigo', isRunning: true },
      running: { text: '执行中', color: 'processing', isRunning: true },
      finished: { text: '已完成', color: 'success' },
      interrupted: { text: '已中断', color: 'warning' },
    };

    const statusInfo = statusMap[run.status] || {
      text: run.status,
      color: 'default',
    };

    return (
      <Space size={6}>
        <Tag
          color={statusInfo.color}
          style={{
            borderRadius: 4,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {statusInfo.isRunning ? <span className="pulse-dot" /> : null}
          {statusInfo.text}
        </Tag>
        {run.result === 'passed' ? (
          <Tag color="success" style={{ borderRadius: 4 }}>
            通过
          </Tag>
        ) : run.result === 'failed' ? (
          <Tag color="error" style={{ borderRadius: 4 }}>
            失败
          </Tag>
        ) : null}
        {run.deviceLabel ? (
          <Tag
            color="default"
            style={{
              borderRadius: 4,
              maxWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {run.deviceLabel}
          </Tag>
        ) : null}
      </Space>
    );
  };

  return (
    <>
      {/* Metrics Ribbon */}
      <div className="metrics-ribbon">
        <div className="metric-card">
          <div className="metric-info">
            <span className="metric-label">任务总数</span>
            <span className="metric-value">{metrics.total}</span>
          </div>
          <div className="metric-icon-wrap metric-icon-indigo">
            <AppstoreOutlined />
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-info">
            <span className="metric-label">已生成用例</span>
            <span className="metric-value">{metrics.checklistReady}</span>
          </div>
          <div className="metric-icon-wrap metric-icon-cyan">
            <CheckCircleOutlined />
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-info">
            <span className="metric-label">正在执行</span>
            <span
              className="metric-value"
              style={{ color: metrics.running > 0 ? '#0284c7' : undefined }}
            >
              {metrics.running}
            </span>
          </div>
          <div className="metric-icon-wrap metric-icon-amber">
            <SyncOutlined spin={metrics.running > 0} />
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-info">
            <span className="metric-label">测试通过率</span>
            <span className="metric-value" style={{ color: '#059669' }}>
              {metrics.passRate}%
            </span>
          </div>
          <div className="metric-icon-wrap metric-icon-emerald">
            <ThunderboltOutlined />
          </div>
        </div>
      </div>

      {/* Main Workspace Card */}
      <Card className="workspace-card">
        {/* Table Toolbar */}
        <div className="table-toolbar">
          <div className="toolbar-filters">
            <Input
              allowClear
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="搜索测试任务标题..."
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              style={{ width: 240 }}
              value={searchKeyword}
            />
            <Select
              onChange={(value) => setPlatformFilter(value)}
              options={[
                { value: 'all', label: '全部平台' },
                { value: 'android', label: 'Android 安卓' },
                { value: 'ios', label: 'iOS 苹果' },
              ]}
              style={{ width: 150 }}
              value={platformFilter}
            />
          </div>

          {canCreate ? (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setDocxBase64(undefined);
                setDocxFileName(undefined);
                setInputMode('text');
                setOpen(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                border: 'none',
                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)',
              }}
              type="primary"
            >
              新建测试任务
            </Button>
          ) : null}
        </div>

        {/* Task Data Table */}
        <Table
          columns={[
            {
              title: '测试任务',
              dataIndex: 'title',
              render: (title: string, row: TaskSummary) => (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <div
                    style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}
                  >
                    {title}
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Tag
                      color={row.inputType === 'docx' ? 'blue' : 'default'}
                      style={{ fontSize: 11, borderRadius: 4 }}
                    >
                      {row.inputType === 'docx' ? (
                        <>
                          <FileWordOutlined style={{ marginRight: 3 }} />
                          Word 规格文档
                        </>
                      ) : (
                        <>
                          <FileTextOutlined style={{ marginRight: 3 }} />
                          文本需求
                        </>
                      )}
                    </Tag>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      由 {row.createdByName || row.createdBy} 创建
                    </span>
                  </div>
                </div>
              ),
            },
            {
              title: '目标平台',
              dataIndex: 'platform',
              width: 140,
              render: (platform: TaskPlatform) =>
                platform === 'android' ? (
                  <Tag
                    color="success"
                    icon={<AndroidOutlined />}
                    style={{ borderRadius: 4 }}
                  >
                    Android
                  </Tag>
                ) : (
                  <Tag
                    color="processing"
                    icon={<AppleOutlined />}
                    style={{ borderRadius: 4 }}
                  >
                    iOS
                  </Tag>
                ),
            },
            {
              title: '自动化用例',
              dataIndex: 'hasChecklist',
              width: 150,
              render: (hasChecklist: boolean) =>
                hasChecklist ? (
                  <Tag
                    color="success"
                    icon={<CheckCircleOutlined />}
                    style={{ borderRadius: 4 }}
                  >
                    已生成用例
                  </Tag>
                ) : (
                  <Tag
                    color="warning"
                    icon={<ClockCircleOutlined />}
                    style={{ borderRadius: 4 }}
                  >
                    待生成
                  </Tag>
                ),
            },
            {
              title: '最新执行状态',
              width: 220,
              render: (_, row: TaskSummary) => renderRunStatus(row.currentRun),
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              width: 170,
              render: (dateStr: string) => (
                <span style={{ fontSize: 12.5, color: '#64748b' }}>
                  {new Date(dateStr).toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              ),
            },
            {
              title: '操作',
              width: 210,
              render: (_, row: TaskSummary) => (
                <Space size="middle">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(row.id);
                    }}
                    size="small"
                    type="link"
                  >
                    查看详情
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(row.id);
                    }}
                    size="small"
                    type="link"
                  >
                    编辑
                  </Button>
                  <Tooltip
                    title={
                      isActiveRun(row.currentRun?.status)
                        ? '请先在 Studio 停止任务，再删除'
                        : undefined
                    }
                  >
                    <Button
                      danger
                      aria-disabled={isActiveRun(row.currentRun?.status)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isActiveRun(row.currentRun?.status)) {
                          message.warning('请先在 Studio 停止任务，再删除');
                          return;
                        }
                        setDeletingTask(row);
                      }}
                      size="small"
                      type="link"
                    >
                      删除
                    </Button>
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          dataSource={filteredTasks}
          locale={{
            emptyText: (
              <Empty
                description={
                  searchKeyword || platformFilter !== 'all'
                    ? '没有找到符合筛选条件的测试任务'
                    : '当前项目暂无测试任务，请点击上方按钮新建测试任务'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          onRow={(row) => ({
            onClick: () => onOpen(row.id),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `共 ${total} 条测试任务`,
            showQuickJumper: true,
          }}
          rowKey="id"
        />
      </Card>

      <Modal
        title="删除任务"
        open={deletingTask !== null}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        cancelButtonProps={{ disabled: deleting }}
        confirmLoading={deleting}
        closable={!deleting}
        maskClosable={!deleting}
        keyboard={!deleting}
        onCancel={() => {
          if (!deleting) setDeletingTask(null);
        }}
        onOk={async () => {
          if (!deletingTask || deleting) return;
          setDeleting(true);
          try {
            await api.deleteTask(deletingTask.id);
            onDeleted(deletingTask.id);
            setDeletingTask(null);
            message.success('任务已删除');
          } catch (error) {
            message.error(
              error instanceof Error ? error.message : '删除失败，请重试',
            );
          } finally {
            setDeleting(false);
          }
        }}
      >
        <p>确定删除「{deletingTask?.title}」？</p>
        <p>平台上的执行历史、报告和上传附件将一并删除，无法恢复。</p>
      </Modal>

      {/* New Task Modal */}
      <Modal
        confirmLoading={submitting}
        destroyOnClose
        okText="立即创建"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        open={open}
        title="新建测试任务"
        width={600}
      >
        <Form
          form={form}
          initialValues={{ platform: 'android' }}
          layout="vertical"
          onFinish={async (values: {
            title: string;
            platform: TaskPlatform;
            text?: string;
          }) => {
            if (inputMode === 'docx' && !docxBase64) {
              message.error('请先选择并上传 Word (.docx) 需求规格文档');
              return;
            }
            if (inputMode === 'text' && (!values.text || !values.text.trim())) {
              message.error('请输入需求描述文本');
              return;
            }

            setSubmitting(true);
            try {
              await api.createTask(projectId, {
                title: values.title.trim(),
                platform: values.platform,
                text: inputMode === 'text' ? values.text : undefined,
                docxBase64: inputMode === 'docx' ? docxBase64 : undefined,
              });
              message.success('测试任务创建成功');
              setOpen(false);
              setDocxBase64(undefined);
              setDocxFileName(undefined);
              await onCreated();
            } catch (error) {
              message.error(
                error instanceof Error ? error.message : '创建任务失败',
              );
            } finally {
              setSubmitting(false);
            }
          }}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="测试任务标题"
            name="title"
            rules={[{ required: true, message: '请输入任务标题' }]}
          >
            <Input
              placeholder="例如：用户下单与在线支付全链路回归测试"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="目标移动平台"
            name="platform"
            rules={[{ required: true, message: '请选择目标测试平台' }]}
          >
            <Radio.Group buttonStyle="solid" size="middle">
              <Radio.Button value="android">
                <AndroidOutlined style={{ marginRight: 6 }} />
                Android 安卓
              </Radio.Button>
              <Radio.Button value="ios">
                <AppleOutlined style={{ marginRight: 6 }} />
                iOS 苹果
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: '#1e293b',
                display: 'block',
                marginBottom: 8,
              }}
            >
              需求规格来源
            </span>
            <Segmented
              block
              onChange={(value) => setInputMode(value as 'text' | 'docx')}
              options={[
                {
                  label: (
                    <Space>
                      <FileTextOutlined />
                      直接输入需求文本
                    </Space>
                  ),
                  value: 'text',
                },
                {
                  label: (
                    <Space>
                      <FileWordOutlined />
                      上传 Word 需求文档 (.docx)
                    </Space>
                  ),
                  value: 'docx',
                },
              ]}
              value={inputMode}
            />
          </div>

          {inputMode === 'text' ? (
            <Form.Item
              extra="详细描述测试场景、业务逻辑、预期验证点，多模态 AI 模型将据此解析用例。"
              name="text"
            >
              <Input.TextArea
                placeholder="请输入业务功能需求说明、前置条件与预期测试验证项..."
                rows={8}
              />
            </Form.Item>
          ) : (
            <Form.Item extra="支持上传标准 Word 规格说明书 (.docx)，云平台将自动解析其中的需求章节与表格。">
              <Upload.Dragger
                accept=".docx"
                beforeUpload={async (file) => {
                  try {
                    const base64 = await readFileAsBase64(file);
                    setDocxBase64(base64);
                    setDocxFileName(file.name);
                    message.success(`已载入文件: ${file.name}`);
                  } catch (err) {
                    message.error('读取文档失败，请检查文件格式');
                  }
                  return false;
                }}
                fileList={
                  docxFileName
                    ? [
                        {
                          uid: '-1',
                          name: docxFileName,
                          status: 'done',
                        },
                      ]
                    : []
                }
                maxCount={1}
                onRemove={() => {
                  setDocxBase64(undefined);
                  setDocxFileName(undefined);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: '#4f46e5' }} />
                </p>
                <p
                  className="ant-upload-text"
                  style={{ fontSize: 14, fontWeight: 500 }}
                >
                  点击或拖拽 Word (.docx) 需求文档至此处
                </p>
                <p
                  className="ant-upload-hint"
                  style={{ fontSize: 12, color: '#94a3b8' }}
                >
                  支持 PRD 需求文档、功能清单、业务规格说明书
                </p>
              </Upload.Dragger>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}

function EditTaskModal({
  taskId,
  open,
  onCancel,
  onUpdated,
}: {
  taskId: string | null;
  open: boolean;
  onCancel: () => void;
  onUpdated: (task: TaskDetail) => Promise<void> | void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'docx'>('text');
  const [docxBase64, setDocxBase64] = useState<string>();
  const [docxFileName, setDocxFileName] = useState<string>();
  const [initialTask, setInitialTask] = useState<TaskDetail | null>(null);

  useEffect(() => {
    if (!open || !taskId) {
      setInitialTask(null);
      setDocxBase64(undefined);
      setDocxFileName(undefined);
      form.resetFields();
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .task(taskId)
      .then((res) => {
        if (cancelled) return;
        setInitialTask(res.task);
        setInputMode(res.task.inputType);
        form.setFieldsValue({
          title: res.task.title,
          platform: res.task.platform,
          text: res.task.sourceText,
          resetChecklist: false,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        message.error(err instanceof Error ? err.message : '获取任务详情失败');
        onCancel();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, taskId, form, onCancel]);

  const handleFinish = async (values: {
    title: string;
    platform: TaskPlatform;
    text?: string;
    resetChecklist?: boolean;
  }) => {
    if (!taskId) return;
    if (inputMode === 'text' && (!values.text || !values.text.trim())) {
      message.error('请输入需求描述文本');
      return;
    }
    if (inputMode === 'docx' && !docxBase64 && !initialTask?.docxRelativePath) {
      message.error('请上传 Word (.docx) 需求规格文档');
      return;
    }

    if (
      values.resetChecklist &&
      !window.confirm(
        '确定清空用例清单？所有人工修改都将丢失，下次 Studio 运行会重新生成。',
      )
    ) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.updateTask(taskId, {
        title: values.title.trim(),
        platform: values.platform,
        text: inputMode === 'text' ? values.text : undefined,
        docxBase64: inputMode === 'docx' && docxBase64 ? docxBase64 : undefined,
        resetChecklist: values.resetChecklist,
        expectedChecklistRevision: initialTask?.checklistRevision,
      });
      message.success('测试任务更新成功');
      await onUpdated(res.task);
      onCancel();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '更新测试任务失败',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      confirmLoading={submitting}
      destroyOnClose
      okText="保存修改"
      onCancel={onCancel}
      onOk={() => form.submit()}
      open={open}
      title="编辑测试任务"
      width={600}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin tip="正在加载任务详情..." />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="测试任务标题"
            name="title"
            rules={[{ required: true, message: '请输入任务标题' }]}
          >
            <Input
              placeholder="例如：用户下单与在线支付全链路回归测试"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="目标移动平台"
            name="platform"
            rules={[{ required: true, message: '请选择目标测试平台' }]}
          >
            <Radio.Group buttonStyle="solid" size="middle">
              <Radio.Button value="android">
                <AndroidOutlined style={{ marginRight: 6 }} />
                Android 安卓
              </Radio.Button>
              <Radio.Button value="ios">
                <AppleOutlined style={{ marginRight: 6 }} />
                iOS 苹果
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: '#1e293b',
                display: 'block',
                marginBottom: 8,
              }}
            >
              需求规格来源
            </span>
            <Segmented
              block
              onChange={(value) => setInputMode(value as 'text' | 'docx')}
              options={[
                {
                  label: (
                    <Space>
                      <FileTextOutlined />
                      直接输入需求文本
                    </Space>
                  ),
                  value: 'text',
                },
                {
                  label: (
                    <Space>
                      <FileWordOutlined />
                      上传 Word 需求文档 (.docx)
                    </Space>
                  ),
                  value: 'docx',
                },
              ]}
              value={inputMode}
            />
          </div>

          {inputMode === 'text' ? (
            <Form.Item
              extra="详细描述测试场景、业务逻辑、预期验证点，多模态 AI 模型将据此解析用例。"
              name="text"
            >
              <Input.TextArea
                placeholder="请输入业务功能需求说明、前置条件与预期测试验证项..."
                rows={8}
              />
            </Form.Item>
          ) : (
            <Form.Item extra="支持上传标准 Word 规格说明书 (.docx)，云平台将自动解析其中的需求章节与表格。">
              {initialTask?.docxRelativePath && !docxFileName ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: '8px 12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Space>
                    <FileWordOutlined style={{ color: '#2563eb' }} />
                    <span style={{ fontSize: 13, color: '#334155' }}>
                      当前已关联规格文档（如需替换可直接在下方上传新文件）
                    </span>
                  </Space>
                  <Tag color="blue">已有文档</Tag>
                </div>
              ) : null}
              <Upload.Dragger
                accept=".docx"
                beforeUpload={async (file) => {
                  try {
                    const base64 = await readFileAsBase64(file);
                    setDocxBase64(base64);
                    setDocxFileName(file.name);
                    message.success(`已载入文件: ${file.name}`);
                  } catch {
                    message.error('读取文档失败，请检查文件格式');
                  }
                  return false;
                }}
                fileList={
                  docxFileName
                    ? [
                        {
                          uid: '-1',
                          name: docxFileName,
                          status: 'done',
                        },
                      ]
                    : []
                }
                maxCount={1}
                onRemove={() => {
                  setDocxBase64(undefined);
                  setDocxFileName(undefined);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: '#4f46e5' }} />
                </p>
                <p
                  className="ant-upload-text"
                  style={{ fontSize: 14, fontWeight: 500 }}
                >
                  点击或拖拽新 Word (.docx) 需求文档至此处替换
                </p>
                <p
                  className="ant-upload-hint"
                  style={{ fontSize: 12, color: '#94a3b8' }}
                >
                  支持 PRD 需求文档、功能清单、业务规格说明书
                </p>
              </Upload.Dragger>
            </Form.Item>
          )}

          {initialTask?.hasChecklist ? (
            <Form.Item
              extra="默认保留清单及人工修改。清空后，下次 Studio 运行会重新生成；任务认领或执行期间不可清空。"
              name="resetChecklist"
              valuePropName="checked"
            >
              <Checkbox disabled={isActiveRun(initialTask.currentRun?.status)}>
                清空清单并在下次运行时重新生成
              </Checkbox>
            </Form.Item>
          ) : null}
        </Form>
      )}
    </Modal>
  );
}

function TaskDetailView({
  task,
  onBack,
  onRefresh,
  onEdit,
  onChecklistSaved,
  token,
}: {
  task: TaskDetail;
  onBack: () => void;
  onRefresh: () => Promise<void>;
  onEdit: () => void;
  onChecklistSaved: (state: ChecklistState) => void;
  token: string;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(false);
  const reportRun = [...task.runs]
    .reverse()
    .find((run) => run.uploadStatus === 'uploaded');

  const handleCopy = (content: string, typeName: string) => {
    navigator.clipboard
      .writeText(content)
      .then(() => message.success(`已复制${typeName}到剪贴板`))
      .catch(() => message.error('复制失败，请手动选择复制'));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
      message.success('已刷新最新任务详情与执行记录');
    } finally {
      setRefreshing(false);
    }
  };

  const statusMap: Record<RunStatus, { text: string; color: string }> = {
    claimed: { text: '已认领', color: 'purple' },
    preparing: { text: '准备中', color: 'indigo' },
    running: { text: '执行中', color: 'processing' },
    finished: { text: '已完成', color: 'success' },
    interrupted: { text: '已中断', color: 'warning' },
  };

  const uploadStatusMap: Record<UploadStatus, { text: string; color: string }> =
    {
      pending: { text: '待上传', color: 'default' },
      uploading: { text: '上传中', color: 'processing' },
      uploaded: { text: '报告已就绪', color: 'success' },
      failed: { text: '上传失败', color: 'error' },
    };

  return (
    <div>
      {editingChecklist && (
        <ChecklistEditor
          task={task}
          onClose={() => setEditingChecklist(false)}
          onSaved={(state) => {
            onChecklistSaved(state);
            message.success('清单已保存，下次运行生效');
          }}
          onRefresh={onRefresh}
        />
      )}
      {/* Navigation & Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          返回任务列表
        </Button>
        <Space>
          <Button icon={<EditOutlined />} onClick={onEdit} type="primary">
            编辑任务
          </Button>
          <Button
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
          >
            刷新任务状态
          </Button>
        </Space>
      </div>

      {/* Task Overview Card */}
      <Card className="workspace-card" style={{ marginBottom: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <Typography.Title level={4} style={{ margin: 0 }}>
                {task.title}
              </Typography.Title>
              {task.platform === 'android' ? (
                <Tag
                  color="success"
                  icon={<AndroidOutlined />}
                  style={{ borderRadius: 4 }}
                >
                  Android
                </Tag>
              ) : (
                <Tag
                  color="processing"
                  icon={<AppleOutlined />}
                  style={{ borderRadius: 4 }}
                >
                  iOS
                </Tag>
              )}
              <Tag
                color={task.inputType === 'docx' ? 'blue' : 'default'}
                style={{ borderRadius: 4 }}
              >
                {task.inputType === 'docx' ? 'Word 规格解析' : '文本输入需求'}
              </Tag>
              {task.hasChecklist ? (
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{ borderRadius: 4 }}
                >
                  用例清单已就绪
                </Tag>
              ) : (
                <Tag
                  color="warning"
                  icon={<ClockCircleOutlined />}
                  style={{ borderRadius: 4 }}
                >
                  待生成用例
                </Tag>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
              创建人：
              <span style={{ color: '#1e293b', fontWeight: 500 }}>
                {task.createdByName || task.createdBy}
              </span>
              {' · '}
              创建时间：{new Date(task.createdAt).toLocaleString('zh-CN')}
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs for PRD, Checklist, and Runs */}
      <Card className="workspace-card">
        <Tabs
          defaultActiveKey="checklist"
          items={[
            {
              key: 'checklist',
              label: (
                <Space>
                  <FileDoneOutlined />
                  自动化用例清单 (Checklist YAML)
                  {task.yaml ? <Badge status="success" /> : null}
                </Space>
              ),
              children: (
                <div>
                  {task.yaml ? (
                    <div>
                      <Space style={{ marginBottom: 12 }}>
                        <Button
                          icon={<EditOutlined />}
                          type="primary"
                          disabled={
                            !task.checklist ||
                            isActiveRun(task.currentRun?.status)
                          }
                          onClick={() => setEditingChecklist(true)}
                        >
                          编辑用例清单
                        </Button>
                        {isActiveRun(task.currentRun?.status) && (
                          <span>任务已认领或正在执行，结束后可编辑</span>
                        )}
                      </Space>
                      <div className="spec-code-card">
                        <div className="spec-code-header">
                          <span className="spec-code-title">
                            checklist.yaml
                          </span>
                          <Button
                            ghost
                            icon={<CopyOutlined />}
                            onClick={() =>
                              handleCopy(task.yaml || '', '用例清单 YAML')
                            }
                            size="small"
                            style={{ color: '#c9d1d9', borderColor: '#30363d' }}
                          >
                            复制 YAML
                          </Button>
                        </div>
                        <pre className="spec-code-body">{task.yaml}</pre>
                      </div>
                      <div
                        style={{
                          marginTop: 12,
                          fontSize: 13,
                          color: '#64748b',
                        }}
                      >
                        YAML
                        为只读预览，自动与清单同步。请通过“编辑用例清单”修改，保存后下次
                        Studio 运行生效。
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state-box">
                      <RobotOutlined
                        style={{
                          fontSize: 42,
                          color: '#818cf8',
                          marginBottom: 12,
                        }}
                      />
                      <Typography.Title level={5} style={{ color: '#1e293b' }}>
                        尚未生成自动化测试用例清单
                      </Typography.Title>
                      <Typography.Paragraph
                        style={{
                          color: '#64748b',
                          maxWidth: 460,
                          margin: '0 auto 16px',
                        }}
                      >
                        请在 <strong>CarbsiLab Studio</strong>{' '}
                        桌面端认领此任务，多模态 AI 引擎将自动理解需求描述并生成
                        Midscene 自动化测试步骤。
                      </Typography.Paragraph>
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'prd',
              label: (
                <Space>
                  <FileTextOutlined />
                  需求规格说明 (PRD)
                </Space>
              ),
              children: (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      原始需求内容（共{' '}
                      {task.sourceText ? task.sourceText.length : 0} 字）
                    </span>
                    {task.sourceText ? (
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() =>
                          handleCopy(task.sourceText, '需求规格内容')
                        }
                        size="small"
                      >
                        复制需求内容
                      </Button>
                    ) : null}
                  </div>
                  <pre className="prd-text-box">
                    {task.sourceText || '暂无需求文本描述'}
                  </pre>
                </div>
              ),
            },
            {
              key: 'runs',
              label: (
                <Space>
                  <MobileOutlined />
                  执行记录与可视化报告 ({task.runs.length})
                </Space>
              ),
              children: (
                <div>
                  <Table
                    columns={[
                      {
                        title: '测试设备',
                        dataIndex: 'deviceLabel',
                        render: (text: string) => (
                          <Space>
                            <MobileOutlined style={{ color: '#4f46e5' }} />
                            <span style={{ fontWeight: 600 }}>
                              {text || '未知设备'}
                            </span>
                          </Space>
                        ),
                      },
                      {
                        title: '认领执行人',
                        dataIndex: 'claimedByName',
                        width: 140,
                        render: (_: unknown, row: RunSummary) =>
                          row.claimedByName || row.claimedBy || '未知',
                      },
                      {
                        title: '执行状态',
                        dataIndex: 'status',
                        width: 130,
                        render: (status: RunStatus) => {
                          const info = statusMap[status] || {
                            text: status,
                            color: 'default',
                          };
                          return (
                            <Tag color={info.color} style={{ borderRadius: 4 }}>
                              {info.text}
                            </Tag>
                          );
                        },
                      },
                      {
                        title: '测试结果',
                        dataIndex: 'result',
                        width: 120,
                        render: (result: TestResult | null) =>
                          result === 'passed' ? (
                            <Tag
                              color="success"
                              icon={<CheckCircleOutlined />}
                              style={{ borderRadius: 4 }}
                            >
                              全部通过
                            </Tag>
                          ) : result === 'failed' ? (
                            <Tag
                              color="error"
                              icon={<CloseCircleOutlined />}
                              style={{ borderRadius: 4 }}
                            >
                              存在失败
                            </Tag>
                          ) : (
                            <Tag color="default" style={{ borderRadius: 4 }}>
                              —
                            </Tag>
                          ),
                      },
                      {
                        title: '报告状态',
                        dataIndex: 'uploadStatus',
                        width: 130,
                        render: (status: UploadStatus) => {
                          const info = uploadStatusMap[status] || {
                            text: status,
                            color: 'default',
                          };
                          return (
                            <Tag color={info.color} style={{ borderRadius: 4 }}>
                              {info.text}
                            </Tag>
                          );
                        },
                      },
                      {
                        title: '认领时间',
                        dataIndex: 'claimedAt',
                        width: 170,
                        render: (dateStr: string) => (
                          <span style={{ fontSize: 12, color: '#64748b' }}>
                            {dateStr
                              ? new Date(dateStr).toLocaleString('zh-CN')
                              : '—'}
                          </span>
                        ),
                      },
                    ]}
                    dataSource={task.runs}
                    locale={{
                      emptyText: (
                        <Empty
                          description="尚未有设备客户端认领并执行此任务"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ),
                    }}
                    pagination={false}
                    rowKey="id"
                  />

                  {/* Embedded Interactive Report View */}
                  {reportRun ? (
                    <div className="report-container-card">
                      <div className="report-header-bar">
                        <Space>
                          <ThunderboltOutlined style={{ color: '#4f46e5' }} />
                          <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                            多模态测试可视化运行报告 ({reportRun.deviceLabel})
                          </span>
                        </Space>
                        <Space>
                          <Button
                            href={reportUrl(reportRun.id)}
                            icon={<ExportOutlined />}
                            size="small"
                            target="_blank"
                          >
                            在新标签页全屏打开
                          </Button>
                        </Space>
                      </div>
                      <iframe
                        className="report-iframe"
                        sandbox="allow-scripts allow-same-origin"
                        src={`/api/runs/${reportRun.id}/report?token=${encodeURIComponent(token)}`}
                        title="测试运行报告"
                      />
                    </div>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}

function MembersView({ projectId }: { projectId: string }) {
  const [members, setMembers] = useState<PublicUser[]>([]);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [memberResult, userResult] = await Promise.all([
        api.members(projectId),
        api.users(),
      ]);
      setMembers(memberResult.members);
      setUsers(userResult.users);
    } catch (err) {
      message.error('加载项目成员失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const candidateUsers = useMemo(() => {
    return users.filter((u) => !members.some((m) => m.id === u.id));
  }, [users, members]);

  return (
    <Card className="workspace-card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            项目成员管理
          </Typography.Title>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            维护参与当前项目测试任务制定、执行与报告审阅的成员清单
          </div>
        </div>

        <Space>
          <Select
            allowClear
            onChange={(val) => setSelectedUserId(val)}
            options={candidateUsers.map((u) => ({
              value: u.id,
              label: `${u.username} (${u.role === 'admin' ? '系统管理员' : '测试成员'})`,
            }))}
            placeholder="选择系统用户加入项目"
            style={{ width: 260 }}
            value={selectedUserId}
          />
          <Button
            disabled={!selectedUserId}
            icon={<PlusOutlined />}
            onClick={async () => {
              if (!selectedUserId) return;
              try {
                await api.addMember(projectId, selectedUserId);
                message.success('已成功添加项目成员');
                setSelectedUserId(undefined);
                await reload();
              } catch (err) {
                message.error('添加项目成员失败');
              }
            }}
            type="primary"
          >
            添加成员
          </Button>
        </Space>
      </div>

      <Table
        columns={[
          {
            title: '用户名',
            dataIndex: 'username',
            render: (username: string) => (
              <Space>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#e0e7ff',
                    color: '#4338ca',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {username.charAt(0).toUpperCase()}
                </div>
                <span style={{ fontWeight: 600 }}>{username}</span>
              </Space>
            ),
          },
          {
            title: '系统权限角色',
            dataIndex: 'role',
            render: (role: PublicUser['role']) =>
              role === 'admin' ? (
                <Tag color="purple" style={{ borderRadius: 4 }}>
                  系统管理员
                </Tag>
              ) : (
                <Tag color="blue" style={{ borderRadius: 4 }}>
                  测试工程师
                </Tag>
              ),
          },
          {
            title: '用户 ID',
            dataIndex: 'id',
            render: (id: string) => (
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#94a3b8',
                }}
              >
                {id}
              </span>
            ),
          },
        ]}
        dataSource={members}
        loading={loading}
        pagination={false}
        rowKey="id"
      />
    </Card>
  );
}

function UsersView({
  users,
  onCreated,
}: {
  users: PublicUser[];
  onCreated: (user: PublicUser) => void;
}) {
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);

  return (
    <Card className="workspace-card">
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          系统全局用户管理
        </Typography.Title>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
          管理员可在此创建全平台账号，并指派管理员或测试成员权限
        </div>
      </div>

      <Form
        form={form}
        initialValues={{ role: 'member' }}
        layout="inline"
        onFinish={async (values: {
          username: string;
          password: string;
          role: PublicUser['role'];
        }) => {
          setCreating(true);
          try {
            const result = await api.createUser(values);
            message.success(`用户「${result.user.username}」创建成功`);
            form.resetFields();
            onCreated(result.user);
          } catch (error) {
            message.error(
              error instanceof Error ? error.message : '创建用户失败',
            );
          } finally {
            setCreating(false);
          }
        }}
        style={{ marginBottom: 24 }}
      >
        <Form.Item
          name="username"
          rules={[{ required: true, message: '请输入用户名' }]}
        >
          <Input
            placeholder="新用户名"
            prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true, message: '请输入初始密码' }]}
        >
          <Input.Password
            placeholder="初始密码"
            prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
          />
        </Form.Item>
        <Form.Item name="role">
          <Select
            options={[
              { value: 'member', label: '测试工程师 (Member)' },
              { value: 'admin', label: '系统管理员 (Admin)' },
            ]}
            style={{ width: 180 }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            icon={<PlusOutlined />}
            loading={creating}
            type="primary"
            htmlType="submit"
          >
            新建用户
          </Button>
        </Form.Item>
      </Form>

      <Table
        columns={[
          {
            title: '用户名',
            dataIndex: 'username',
            render: (username: string) => (
              <Space>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#ede9fe',
                    color: '#6d28d9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {username.charAt(0).toUpperCase()}
                </div>
                <span style={{ fontWeight: 600 }}>{username}</span>
              </Space>
            ),
          },
          {
            title: '权限角色',
            dataIndex: 'role',
            render: (role: PublicUser['role']) =>
              role === 'admin' ? (
                <Tag color="purple" style={{ borderRadius: 4 }}>
                  系统管理员
                </Tag>
              ) : (
                <Tag color="blue" style={{ borderRadius: 4 }}>
                  测试工程师
                </Tag>
              ),
          },
          {
            title: '用户全局唯一标识 (ID)',
            dataIndex: 'id',
            render: (id: string) => (
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#94a3b8',
                }}
              >
                {id}
              </span>
            ),
          },
        ]}
        dataSource={users}
        pagination={{ pageSize: 8 }}
        rowKey="id"
      />
    </Card>
  );
}
